import React, { useState } from 'react';
import {
  Button, Col, Row, Spin,
  Table, Form,
  notification,
  DatePicker,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import './BankPaymentSlip.scss';
import { FilePdfOutlined } from '@ant-design/icons';
import FilterIcon from 'mdi-react/FilterIcon';
import moment, { Moment } from 'moment';
import Input from 'antd/lib/input/Input';
import {
  get, isSuperAgentError, patch, RequestWrapper, ResponseWrapper,
} from '../../dataSource/requests';
import { downloadPdf, downloadPdfIr } from '../../utils/helpers';
import { getUserBP } from '../../utils/users';

interface TablePagination {
  current: number | undefined,
  defaultCurrent: number | undefined,
  pageSize: number | undefined,
  showQuickJumper: boolean | {
    goButton?: React.ReactNode;
  },
  total: number | undefined,
  itemsInPage?: number | undefined,
}

type TResquestBillet = {
  billetValue: string
  companyName: string
  cooperativeName: string
  dueDateFrom: string
  dueDateTo: string
  emissionDateFrom: string
  emissionDateTo: string
  installments: string
  invoiceNumber: string
  billetPdfId?:string
}

const BankPaymentSlip = (): JSX.Element => {
  const navigate = useNavigate();

  const [formInstance] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [tableDataSource, setTableDataSource] = useState<Array<TResquestBillet>>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tablePagination, setTablePagination] = useState<TablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 15,
    showQuickJumper: true,
    total: 0,
  });

  const dowloadPdf = async (pdfIdentity:number):Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    setLoading(true);

    const requestReference: RequestWrapper = get({
      url: `/billets/${pdfIdentity}`,
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 23001):
        downloadPdf(response?.body?.billetPdfBase64, `boleto-${moment().format('YYYY-MM-DDTHH:mm:ss')}`);
        break;

      case (responseError?.body?.code === -24003): {
        notification.error({
          message: 'Erro',
          description: 'O pdf do imposto de renda não foi encontrado.',
        });
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao baixar o arquivo, por favor tente novamente mais tarde.',
        });
        break;
    }

    setLoading(false);
  };

  const getValuesForm = ():TResquestBillet => {
    const values = formInstance.getFieldsValue();

    return {
      billetValue: values.billetValue ? values.billetValue : '',
      companyName: values.companyName ? values.companyName : '',
      cooperativeName: values.cooperativeName ? values.cooperativeName : '',
      dueDateFrom: moment(values.date[0]).startOf('day').format('yyyy-MM-DDTHH:mm:ss.SSSZ'),
      dueDateTo: moment(values.date[1]).startOf('day').format('yyyy-MM-DDTHH:mm:ss.SSSZ'),
      emissionDateFrom: values.dateInitialIssue?.length ? moment(values.dateInitialIssue[0], 'YYYY-MM-DDTHH:mm:ss.SSS').startOf('day').toISOString() : '',
      emissionDateTo: values.dateInitialIssue?.length ? moment(values.dateInitialIssue[1], 'YYYY-MM-DDTHH:mm:ss.SSS').startOf('day').toISOString() : '',
      installments: values.installments ? values.installments : '',
      invoiceNumber: values.invoiceNumber ? values.invoiceNumber : '',
    };
  };

  const executeFilter = async (args: {
    page?: number,
    pageSize?: number,
    orderBy?: string,
    orderByDir?: string,
  } = {
    page: 0,
    pageSize: 15,
    orderBy: 'dueDate',
    orderByDir: 'DESC',
  }): Promise<void> => {
    setTableLoading(true);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    const formatValues = getValuesForm();
    const bp = getUserBP();

    const argsQuery = {
      orderBy: ('orderBy' in args) ? args.orderBy : 'dueDate',
      page: ('page' in args && args.page >= 1) ? args?.page : 0,
      pageSize: ('pageSize' in args) ? args.pageSize : 15,
      sort: ('orderByDir' in args) ? args.orderByDir : 'DESC',
    };

    const requestReference: RequestWrapper = patch({
      url: '/billets',
      params: {
        ...Object.fromEntries(
          Object
            .entries(formatValues)
            .filter(
              ([, elementValue]) => {
                return elementValue !== '';
              },
            ),
        ),
      },
      query: {
        ...Object.fromEntries(
          Object
            .entries(argsQuery)
            .filter(
              ([, elementValue]) => {
                return elementValue !== null;
              },
            ),
        ),
      },
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 23000): {
        const pagination = tablePagination;
        pagination.pageSize = response?.body?.paginationInfo?.maxSize;
        pagination.current = response?.body?.paginationInfo?.pageNumber + 1;
        pagination.total = response?.body?.paginationInfo?.totalElements;

        if (response?.body?.billetOutput.length > 0) {
          setTableDataSource(response?.body?.billetOutput);
        } else {
          setTableDataSource([]);
          notification.info({
            message: 'Atenção',
            description: `Desculpe, não encontramos nenhum boleto para os filtros informados e relacionado ao seu BP ${bp}`,
          });
        }

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: pagination.current,
            defaultCurrent: pagination.defaultCurrent,
            pageSize: pagination.pageSize,
            showQuickJumper: pagination.showQuickJumper,
            total: pagination.total,
          });
        });
        break;
      }

      case (response != null && response?.body?.code === -25002): {
        setTableDataSource([]);
        notification.info({
          message: 'Atenção',
          description: `Desculpe, não encontramos nenhum boleto para os filtros informados e relacionado ao seu BP ${bp}`,
        });
        break;
      }

      case (responseError?.body?.code === -25000): {
        setTableDataSource([]);
        notification.error({
          message: 'Erro',
          description: 'Algo errado aconteceu na solicitação SAP',
        });
        break;
      }

      default: {
        setTableDataSource([]);
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar a lista de boletos, por favor tente novamente mais tarde.',
        });
        break;
      }
    }

    setTableLoading(false);
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any): void => {
    setTableLoading(true);

    setTablePagination((previous) => {
      return ({
        ...previous,
        current: pagination.current || previous.current,
        defaultCurrent: pagination.defaultCurrent || previous.defaultCurrent,
        pageSize: pagination.pageSize || previous.pageSize,
        showQuickJumper: pagination.showQuickJumper
          || previous.showQuickJumper,
        total: pagination.total || previous.total,
      });
    });

    let finalOrderDir;

    if (sorter?.field && sorter?.order) {
      switch (true) {
        case (sorter.order === 'descend'):
          finalOrderDir = 'DESC';
          break;

        case (sorter.order === 'ascend'):
        case (sorter.order == null):
        default:
          finalOrderDir = 'ASC';
          break;
      }
    }

    executeFilter({
      page: (pagination?.current)
        ? pagination?.current - 1
        : 0,
      orderBy: sorter?.field || 'dueDate',
      orderByDir: finalOrderDir || 'DESC',
      pageSize: pagination.pageSize,
    });
  };

  const validateDateRange = (_rule: any, value: Array<Moment>): Promise<void | Error> => {
    if (!value?.[0] && !value?.[1]) {
      return Promise.resolve();
    }

    if (!value?.[0] || !value?.[1]) {
      return Promise.reject(new Error('Selecione as datas de vencimento inicial e final'));
    }

    return Promise.resolve();
  };

  return (
    <div className="BankPaymentSlip">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Row gutter={[24, 24]}>
          <Col xs={{ span: 24 }}>
            <Row
              gutter={[24, 24]}
              justify="space-between"
              className="title-container"
            >
              <Col>
                <span className="page-title">
                  Boletos
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form
          scrollToFirstError
          layout="vertical"
          form={formInstance}
          noValidate
          onFinish={
          async (): Promise<void> => {
            await executeFilter();
          }
        }
        >
          <Row
            gutter={[24, 24]}
            className="filters-container"
          >
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                label="Intervalo da data de vencimento"
                name="date"
                rules={[
                  {
                    required: true,
                    message: 'Selecione as datas de vencimento inicial e final',
                  },
                  {
                    validator: validateDateRange,
                  },
                ]}
                initialValue={[moment().subtract(30, 'day'), moment().add(30, 'day')]}
              >
                <DatePicker.RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Início', 'Fim']}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                label="Intervalo da data de emissão"
                name="dateInitialIssue"
                rules={[
                  {
                    required: false,
                    message: 'Selecione as datas de emissão inicial e final',
                  },
                  {
                    validator: validateDateRange,
                  },
                ]}
              >
                <DatePicker.RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Início', 'Fim']}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                label="Nome da empresa emissora"
                name="companyName"
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o nome da empresa emissora"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item label="Nome do cooperado" name="cooperativeName" rules={[]}>
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o nome do cooperado"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                label="Número da parcela do boleto"
                name="installments"
                rules={[]}
              >
                <Input
                  type="number"
                  style={{ width: '100%' }}
                  placeholder="Digite o número da parcela do boleto"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                label="Número da nota fiscal"
                name="invoiceNumber"
                rules={[
                  {
                    pattern: /^[\w\.,-]+$/,
                    message: '',
                  },
                  {
                    validator: (_rule, value): Promise<void | Error> => {
                      if (value) {
                        const cleanValue = value.replace(/^[0-9,@,.,_,-]+$/, '');

                        if (cleanValue.length > 0) {
                          return Promise.reject(new Error('Utilize apenas números sem pontuação.'));
                        }

                        return Promise.resolve();
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Digite o número da nota fiscal"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 8 }}>
              <Form.Item
                label="Valor do boleto"
                name="billetValue"
                rules={[
                  {
                    pattern: /^(\d{1,3}(\.\d{3})*|\d+)(\.\d{2})?$/,
                    message: 'O valor e limitado em duas casas decimais.',
                  },
                  {
                    validator: (_rule, value): Promise<void | Error> => {
                      if (value) {
                        const cleanValue = value.replace(/^[0-9,@,.,_,-]+$/, '');

                        if (cleanValue.length > 0) {
                          return Promise.reject(new Error('Utilize apenas números sem pontuação.'));
                        }

                        return Promise.resolve();
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  style={{ width: '100%' }}
                  placeholder="Digite o valor do boleto"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item label=" ">
                <Button
                  className="btn-filter"
                  type="primary"
                  htmlType="submit"
                >
                  <FilterIcon />
                  Filtrar
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {tableDataSource.length > 0 && (
          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    columns={[
                      {
                        title: <div className="table-titles">Nome da Empresa Emissora</div>,
                        dataIndex: 'companyName',
                        key: 'companyName',
                        ellipsis: true,
                        sorter: true,
                        width: 240,
                      },
                      {
                        title: <div className="table-titles">Nome do Cooperado</div>,
                        key: 'cooperativeName',
                        dataIndex: 'cooperativeName',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 260,
                      },
                      {
                        title: <div className="table-titles">Data de Emissão</div>,
                        key: 'emissionDate',
                        dataIndex: 'emissionDate',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 185,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.emissionDate) {
                            return moment.utc(record.emissionDate).format('DD/MM/YYYY');
                          }
                          return '';
                        },
                      },
                      {
                        title: <div className="table-titles">Data de Vencimento</div>,
                        key: 'dueDate',
                        dataIndex: 'dueDate',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 190,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.dueDate) {
                            return moment.utc(record.dueDate).format('DD/MM/YYYY');
                          }
                          return '';
                        },
                      },
                      {
                        title: <div className="table-titles">Número da Parcela</div>,
                        key: 'installments',
                        dataIndex: 'installments',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 190,
                      },
                      {
                        title: <div className="table-titles">Número da Nota Fiscal</div>,
                        key: 'invoiceNumber',
                        dataIndex: 'invoiceNumber',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 220,
                      },
                      {
                        title: <div className="table-titles"> Valor do Boleto</div>,
                        key: 'billetValue',
                        dataIndex: 'billetValue',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 160,
                      },
                      {
                        title: <div className="table-titles">Download</div>,
                        dataIndex: 'id',
                        key: 'acoes',
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => (
                          <span>
                            <Button
                              icon={<FilePdfOutlined />}
                              onClick={
                                async (): Promise<void> => {
                                  await dowloadPdf(record.billetPdfId);
                                }
                              }
                            />
                          </span>
                        ),
                        width: 100,
                      },
                    ]}
                    dataSource={tableDataSource}
                    loading={tableLoading}
                    rowKey={(record): string => record.billetPdfId as unknown as string}
                    pagination={{
                      defaultCurrent: tablePagination.defaultCurrent || 1,
                      current: tablePagination.current || 1,
                      pageSize: tablePagination.pageSize || 15,
                      total: tablePagination.total || 0,
                      showQuickJumper: tablePagination.showQuickJumper || true,
                      pageSizeOptions: ['1', '15', '50', '100'],
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    scroll={{
                      x: '100vw',
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Spin>
    </div>
  );
};

export default BankPaymentSlip;
